import { useState, useEffect, useRef, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { supabase } from '../lib/supabase';
import {
  subscriptionService,
  type SubscriptionPlan,
  type SubscriptionPlanDetails,
  type TenantSubscription,
  type SubscriptionUsage
} from '../services/subscriptionService';

export const useSubscription = () => {
  const { tenant } = useTenant();
  const [subscription, setSubscription] = useState<TenantSubscription | null>(null);
  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlanDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSubscriptionData = useCallback(async () => {
    if (!tenant?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [subscriptionData, usageData, plansData] = await Promise.all([
        subscriptionService.getTenantSubscription(tenant.id),
        subscriptionService.getCurrentUsage(tenant.id),
        subscriptionService.getAllPlans()
      ]);

      setSubscription(subscriptionData);
      setUsage(usageData);
      setPlans(plansData);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    if (tenant?.id) {
      loadSubscriptionData();
    }
  }, [tenant?.id, loadSubscriptionData]);

  const subscriptionChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!tenant?.id) return;

    subscriptionChannelRef.current = supabase
      .channel(`tenant-subscription-${tenant.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenants',
          filter: `id=eq.${tenant.id}`,
        },
        () => {
          loadSubscriptionData();
        }
      )
      .subscribe();

    return () => {
      if (subscriptionChannelRef.current) {
        supabase.removeChannel(subscriptionChannelRef.current);
        subscriptionChannelRef.current = null;
      }
    };
  }, [tenant?.id, loadSubscriptionData]);

  const hasFeatureAccess = (feature: string): boolean => {
    if (!subscription) return false;
    return subscriptionService.hasFeatureAccess(subscription.subscription_plan, feature);
  };

  const checkAIQuota = async (featureType: 'ai_quiz' | 'ai_instant_win' | 'ai_game_contest') => {
    if (!tenant?.id) {
      return {
        allowed: false,
        plan: 'free' as SubscriptionPlan,
        limit: 0,
        used: 0,
        remaining: 0,
        reason: 'No tenant selected'
      };
    }

    return await subscriptionService.checkAIQuota(tenant.id, featureType);
  };

  const incrementUsage = async (featureType: 'ai_quiz' | 'ai_instant_win' | 'ai_game_contest') => {
    if (!tenant?.id) return false;

    const success = await subscriptionService.incrementUsage(tenant.id, featureType);
    if (success) {
      await loadSubscriptionData();
    }
    return success;
  };

  const getCurrentPlan = (): SubscriptionPlanDetails | null => {
    if (!subscription) return null;
    return plans.find(p => p.id === subscription.subscription_plan) || null;
  };

  const getUsagePercentage = (featureType: 'ai_quiz' | 'ai_instant_win' | 'ai_game_contest'): number => {
    if (!usage) return 0;

    const currentPlan = getCurrentPlan();
    if (!currentPlan) return 0;

    let limit: number;
    let used: number;

    switch (featureType) {
      case 'ai_quiz':
        limit = currentPlan.limits.ai_quiz_per_month;
        used = usage.ai_quiz_generated;
        break;
      case 'ai_instant_win':
        limit = currentPlan.limits.ai_instant_win_per_month;
        used = usage.ai_instant_win_generated;
        break;
      case 'ai_game_contest':
        limit = currentPlan.limits.ai_game_contest_per_month;
        used = usage.ai_game_contest_generated;
        break;
      default:
        return 0;
    }

    if (limit === -1) return 0;
    if (limit === 0) return 100;

    return Math.min(100, (used / limit) * 100);
  };

  const isOnTrial = (): boolean => {
    if (!subscription || !subscription.trial_end_date) return false;
    return new Date(subscription.trial_end_date) > new Date();
  };

  const getTrialDaysRemaining = (): number => {
    if (!subscription || !subscription.trial_end_date) return 0;
    const trialEnd = new Date(subscription.trial_end_date);
    const now = new Date();
    const diff = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const isPlanActive = (): boolean => {
    return subscription?.subscription_status === 'active' || subscription?.subscription_status === 'trial';
  };

  return {
    subscription,
    usage,
    plans,
    loading,
    error,
    currentPlan: getCurrentPlan(),
    hasFeatureAccess,
    checkAIQuota,
    incrementUsage,
    getUsagePercentage,
    isOnTrial,
    getTrialDaysRemaining,
    isPlanActive,
    refresh: loadSubscriptionData
  };
};
